import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Building2, Landmark, Loader2, Pencil, Plus, Star, Trash2, Upload, User } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import RowActions from './RowActions';
import { BANK_RULES, validate } from '../lib/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Bank fields, in the order of the invoice's PAYMENT DETAILS box. The recipient name (project
// owner) and the payment reference (line descriptions) come from the invoice itself, not from here.
// [key, label translation key (billingProfiles namespace), example placeholder]
export const BANK_FIELDS = [
  ['bank_name', 'bankFields.bank_name', 'Kapitalbank'],
  ['mfo', 'bankFields.mfo', '01158'],
  ['swift', 'bankFields.swift', 'KACHUZ22'],
  ['account', 'bankFields.account', '2020 8840 0000 0000 0001'],
  ['iban', 'bankFields.iban', 'DE89 3704 0044 0532 0130 00'],
  ['bank_address', 'bankFields.bank_address', 'Tashkent, Uzbekistan'],
  ['inn', 'bankFields.inn', '123456789'],
  ['intermediary_bank', 'bankFields.intermediary_bank', 'Citibank N.A., New York'],
  ['intermediary_swift', 'bankFields.intermediary_swift', 'CITIUS33'],
  ['intermediary_account', 'bankFields.intermediary_account', '36310481'],
  ['card_number', 'bankFields.card_number', '8600 1234 5678 9012'],
];

const FIELD_RULES = { email: 'email', phone: 'phone', tax_id: 'tax_id', website: 'website' };

const EMPTY = { kind: 'company', name: '', legal_name: '', email: '', phone: '', address: '', tax_id: '', website: '',
  bank_details: {}, is_default: false };
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

/** Issuer profiles for client invoices: AnyOps (company) or a person, with bank requisites and logo. */
export default function BillingProfilesCard({ user }) {
  const { t } = useTranslation('billingProfiles');
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const canCreate = !['accountant', 'employee', 'pending'].includes(user?.role);
  const [profiles, setProfiles] = useState(null);
  const [editing, setEditing] = useState(null); // profile being edited (or EMPTY for new)
  const [form, setForm] = useState(EMPTY);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoBust, setLogoBust] = useState(0);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const errors = {};
  Object.entries(FIELD_RULES).forEach(([k, rule]) => { const e = validate(rule, form[k]); if (e) errors[k] = e; });
  Object.entries(BANK_RULES).forEach(([k, rule]) => { const e = validate(rule, form.bank_details?.[k]); if (e) errors[`bank.${k}`] = e; });
  if (!String(form.name || '').trim()) errors.name = t('errors.nameRequired');
  const errFor = (k) => ((touched[k] || submitted) ? errors[k] : '');
  const touch = (k) => () => setTouched((prev) => ({ ...prev, [k]: true }));
  const errCls = (k) => (errFor(k) ? 'border-red-500 focus-visible:ring-red-500' : '');
  const ErrText = ({ k }) => (errFor(k) ? <p className="text-xs text-red-600 mt-1">{errFor(k)}</p> : null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/billing-profiles`, { withCredentials: true });
      setProfiles(Array.isArray(res.data) ? res.data : []);
    } catch {
      setProfiles([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = (p) => isAdmin || (p.kind === 'person' && p.user_id === user?.user_id);

  const openNew = () => {
    setForm({ ...EMPTY, kind: isAdmin ? 'company' : 'person', name: isAdmin ? '' : (user?.name || '') });
    setLogoFile(null);
    setTouched({}); setSubmitted(false);
    setEditing(EMPTY);
  };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, bank_details: { ...(p.bank_details || {}) } });
    setLogoFile(null);
    setTouched({}); setSubmitted(false);
    setEditing(p);
  };
  const setBank = (key, value) => setForm((f) => ({ ...f, bank_details: { ...f.bank_details, [key]: value } }));

  const save = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length) { toast.error(Object.values(errors)[0]); return; }
    setSaving(true);
    try {
      const body = {
        kind: form.kind, name: form.name, legal_name: form.legal_name, email: form.email, phone: form.phone,
        address: form.address, tax_id: form.tax_id, website: form.website, bank_details: form.bank_details,
        is_default: form.is_default,
      };
      const res = editing?.id
        ? await axios.patch(`${API_URL}/api/billing-profiles/${editing.id}`, body, { withCredentials: true })
        : await axios.post(`${API_URL}/api/billing-profiles`, body, { withCredentials: true });
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        await axios.post(`${API_URL}/api/billing-profiles/${res.data.id}/logo`, fd, { withCredentials: true });
        setLogoBust((n) => n + 1);
      }
      toast.success(t('toast.saved'));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common:status.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t('confirmDelete', { name: p.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/billing-profiles/${p.id}`, { withCredentials: true });
      toast.success(t('common:status.deleted'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('common:status.deleteFailed')));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Landmark className="h-5 w-5 text-blue-600" /> {t('title')}
        </CardTitle>
        {canCreate && (
          <Button size="sm" onClick={openNew} className="gap-1.5" data-testid="billing-profile-new">
            <Plus className="h-4 w-4" /> {t('addProfile')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('description')}
        </p>
        {profiles === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : profiles.length === 0 ? (
          <p className="text-sm text-slate-500">{t('empty')}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {profiles.map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 flex gap-3">
                <div className="h-12 w-12 shrink-0 rounded-md bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                  {(p.has_logo || p.kind === 'company') ? (
                    <img src={`${API_URL}/api/billing-profiles/${p.id}/logo?v=${logoBust}`} alt="" className="max-h-12 max-w-12 object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : p.kind === 'company' ? <Building2 className="h-5 w-5 text-slate-400" /> : <User className="h-5 w-5 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium truncate">{p.name}</span>
                    {p.is_default && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" title={t('default')} />}
                  </div>
                  <p className="text-xs text-slate-500">{p.kind === 'company' ? t('kind.company') : t('kind.person')}{p.email ? ` · ${p.email}` : ''}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {p.bank_hidden ? t('bankHidden')
                      : (p.bank_details?.bank_name || p.bank_details?.card_number || t('bankMissing'))}
                    {p.bank_details?.account ? ` · ${p.bank_details.account}` : ''}
                  </p>
                </div>
                {canEdit(p) && (
                  <div className="shrink-0">
                    <RowActions actions={[
                      { key: 'edit', label: t('common:actions.edit'), hint: t('actions.editHint'), icon: Pencil, onClick: () => openEdit(p) },
                      { key: 'delete', label: t('common:actions.delete'), hint: t('actions.deleteHint'), icon: Trash2, className: 'text-red-500', onClick: () => remove(p) },
                    ]} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v && !saving) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t('dialog.editTitle') : t('dialog.newTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing?.id && isAdmin && (
              <div className="grid grid-cols-2 gap-2">
                {[['company', 'kindOption.company', Building2], ['person', 'kindOption.person', User]].map(([k, labelKey, Icon]) => (
                  <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, kind: k }))}
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 p-2.5 text-sm ${form.kind === k
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                    <Icon className="h-4 w-4" /> {t(labelKey)}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t('form.name')} *</Label><Input value={form.name} className={errCls('name')} onBlur={touch('name')} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.kind === 'company' ? 'AnyOps Inc' : t('form.personNamePlaceholder')} data-testid="profile-name" /><ErrText k="name" /></div>
              <div className="space-y-1.5"><Label>{t('form.legalName')}</Label><Input value={form.legal_name || ''} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="AnyOps Inc. LLC" /></div>
              <div className="space-y-1.5"><Label>{t('common:fields.email')}</Label><Input type="email" value={form.email || ''} className={errCls('email')} onBlur={touch('email')} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="billing@anyops.uz" data-testid="profile-email" /><ErrText k="email" /></div>
              <div className="space-y-1.5"><Label>{t('common:fields.phone')}</Label><Input type="tel" inputMode="tel" value={form.phone || ''} className={errCls('phone')} onBlur={touch('phone')} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+998 90 123 45 67" data-testid="profile-phone" /><ErrText k="phone" /></div>
              <div className="space-y-1.5"><Label>{t('form.taxId')}</Label><Input value={form.tax_id || ''} className={errCls('tax_id')} onBlur={touch('tax_id')} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="123456789" /><ErrText k="tax_id" /></div>
              <div className="space-y-1.5"><Label>{t('form.website')}</Label><Input value={form.website || ''} className={errCls('website')} onBlur={touch('website')} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="anyops.uz" /><ErrText k="website" /></div>
            </div>
            <div className="space-y-1.5"><Label>{t('form.address')}</Label><Textarea rows={2} value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><Landmark className="h-4 w-4" /> {t('form.bankDetails')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BANK_FIELDS.map(([key, label, example]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs">{t(label)}</Label>
                    <Input value={form.bank_details?.[key] || ''} placeholder={t(`bankExamples.${key}`, { defaultValue: example })} className={errCls(`bank.${key}`)}
                      onBlur={touch(`bank.${key}`)} onChange={(e) => setBank(key, e.target.value)} data-testid={`bank-${key}`} />
                    <ErrText k={`bank.${key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                {t('form.isDefault')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-blue-600">
                <Upload className="h-4 w-4" /> {logoFile ? logoFile.name : t('form.uploadLogo')}
                <input type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
